import type { AgentDetail, AgentRunRow, DispatchRow, ScheduledTaskRow } from '@command-room/shared';

export interface PersonalityBar {
  label: string;
  pct: number;
}

export interface ActivityItem {
  id: string;
  at: string;
  text: string;
  tone: 'ok' | 'failed' | 'info';
}

export interface AgentProfile {
  headline: string | null;
  about: string | null;
  personality: PersonalityBar[];
  whatIDo: string[];
  whatIDontDo: string[];
  goodToKnow: string[];
  tools: string[];
  working: { text: string; at: string | null; live: boolean } | null;
  nextTask: ScheduledTaskRow | null;
  activity: ActivityItem[];
}

const MAX_DO = 6;
const MAX_TOOLS = 12;
const MAX_ACTIVITY = 8;
const LIVE_RUN_WINDOW_MS = 15 * 60 * 1000;
const MAX_ITEM_CHARS = 180;

function clean(text: string | null | undefined): string {
  return (text ?? '').replace(/\r/g, '').trim();
}

/** Strip markdown emphasis and inline code ticks. */
function plain(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1').replace(/\s+/g, ' ').trim();
}

/** First sentence, without cutting at abbreviations like "e.g." or file names. */
export function firstSentence(text: string, max = MAX_ITEM_CHARS): string {
  const flat = plain(text);
  const match = flat.match(/^(.+?[.!?])(?=\s+[A-ZÄÖÜ"'(]|$)/);
  const sentence = match?.[1] ?? flat;
  if (sentence.length <= max) return sentence;
  return `${sentence.slice(0, max - 1).trimEnd()}…`;
}

/** Top-level `- ` bullets; indented continuation lines belong to the previous bullet. */
export function bullets(text: string | null | undefined): string[] {
  const items: string[] = [];
  for (const line of clean(text).split('\n')) {
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      items.push(bullet[1] ?? '');
    } else if (items.length > 0 && /^\s+\S/.test(line)) {
      items[items.length - 1] += ` ${line.trim()}`;
    }
  }
  return items.filter(Boolean);
}

function headlineFrom(role: string | null | undefined, fallback: string | null): string | null {
  const first = firstSentence(clean(role));
  if (!first) return fallback;
  const stripped = first.replace(/^act as\s+(the\s+|an?\s+)?/i, '');
  const text = stripped.charAt(0).toUpperCase() + stripped.slice(1);
  return text.replace(/[.]$/, '');
}

function aboutFrom(personality: string | null | undefined): string | null {
  const text = clean(personality);
  if (!text) return null;
  const traits = text.match(/character traits(?: and tone)?\s*:\s*([\s\S]+)$/i);
  if (traits?.[1]) return plain(traits[1]);
  const kept = text
    .split('\n')
    .filter((line) => {
      const l = line.trim();
      return (
        l &&
        !/^your name is/i.test(l) &&
        !/^age\s*:/i.test(l) &&
        !/^personality profile/i.test(l) &&
        !/^[-*•]\s+.*:\s*\d{1,3}\s*%/.test(l)
      );
    })
    .join(' ');
  return plain(kept) || null;
}

export function personalityBars(personality: string | null | undefined): PersonalityBar[] {
  const bars: PersonalityBar[] = [];
  for (const item of bullets(personality)) {
    const m = item.match(/^([A-Za-zÄÖÜäöüß ]+?)\s*(?:\([^)]*\))?\s*:\s*(\d{1,3})\s*%/);
    if (m?.[1] && m[2]) bars.push({ label: m[1].trim(), pct: Math.min(100, Number(m[2])) });
  }
  return bars;
}

function whatIDoFrom(tasks: string | null | undefined, role: string | null | undefined): string[] {
  const headings: string[] = [];
  for (const line of clean(tasks).split('\n')) {
    const m = line.trim().match(/^\*\*(.+?)\*\*:?\s*$/);
    if (m?.[1]) headings.push(plain(m[1]).replace(/:$/, ''));
  }
  if (headings.length > 0) return headings.slice(0, MAX_DO);
  const paragraphs = clean(role).split(/\n\s*\n/).slice(1);
  return paragraphs.map((p) => firstSentence(p)).filter(Boolean).slice(0, MAX_DO);
}

/** Cut a no-go at its first sentence and drop the dated incident story behind it. */
function nogoFrom(item: string): string {
  return firstSentence(item.replace(/\s+On \d{4}-\d{2}-\d{2}\b.*$/, ''));
}

export function toolsFrom(skillsAuthorities: string | null | undefined): string[] {
  const seen = new Set<string>();
  for (const item of bullets(skillsAuthorities)) {
    for (const m of item.matchAll(/`?\b([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\b`?/g)) {
      if (m[1]) seen.add(m[1]);
    }
  }
  return [...seen].slice(0, MAX_TOOLS);
}

function runText(run: AgentRunRow): string {
  const subject = run.task?.trim() || run.summary?.trim();
  return subject ? firstSentence(subject, 120) : `${run.trigger_type} run`;
}

function isLive(run: AgentRunRow, now: number): boolean {
  if (run.completed_at) return false;
  return now - new Date(run.started_at).getTime() < LIVE_RUN_WINDOW_MS;
}

function workingFrom(runs: AgentRunRow[], now: number): AgentProfile['working'] {
  const latest = runs[0];
  if (!latest) return null;
  return { text: runText(latest), at: latest.completed_at ?? latest.started_at, live: isLive(latest, now) };
}

function nextTaskFrom(tasks: ScheduledTaskRow[]): ScheduledTaskRow | null {
  return tasks.filter((t) => t.enabled).sort((a, b) => a.next_run.localeCompare(b.next_run))[0] ?? null;
}

function runActivity(run: AgentRunRow): ActivityItem {
  const failed = run.status === 'failed';
  const prefix = failed ? 'Run failed' : 'Completed';
  const subject = run.task?.trim() || run.summary?.trim();
  return {
    id: `run-${run.run_id}`,
    at: run.completed_at ?? run.started_at,
    text: subject ? `${prefix}: ${firstSentence(subject, 110)}` : `${prefix} (${run.trigger_type} run)`,
    tone: failed ? 'failed' : 'ok',
  };
}

function dispatchActivity(d: DispatchRow, agent: string): ActivityItem {
  const outgoing = d.from_agent === agent;
  const other = (outgoing ? d.to_agent : d.from_agent) ?? 'someone';
  const task = d.task_summary ? `: ${firstSentence(d.task_summary, 100)}` : '';
  return {
    id: `dispatch-${d.id}`,
    at: d.dispatched_at,
    text: `${outgoing ? `Handed a task to ${other}` : `Got a task from ${other}`}${task}`,
    tone: 'info',
  };
}

export function deriveAgentProfile(detail: AgentDetail, now: number = Date.now()): AgentProfile {
  const spec = detail.spec;
  const activity = [
    ...(detail.recent_runs ?? []).map(runActivity),
    ...(detail.recent_dispatches ?? []).map((d) => dispatchActivity(d, detail.name)),
  ]
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, MAX_ACTIVITY);

  return {
    headline: headlineFrom(spec?.role_text, detail.role),
    about: aboutFrom(spec?.personality_text),
    personality: personalityBars(spec?.personality_text),
    whatIDo: whatIDoFrom(spec?.tasks_text, spec?.role_text),
    whatIDontDo: bullets(spec?.nogos_text).map(nogoFrom).filter(Boolean),
    goodToKnow: bullets(spec?.important_notes_text).map((b) => firstSentence(b)),
    tools: toolsFrom(spec?.skills_authorities_text),
    working: workingFrom(detail.recent_runs ?? [], now),
    nextTask: nextTaskFrom(detail.schedule_tasks ?? []),
    activity,
  };
}
